import { useMemo } from 'react'

import { ROUTES } from 'routes'

import type { Permissions } from 'types'

import { useCompanySeats } from 'hooks/api'
import { useReferralsEnabled } from 'hooks/api/useFeatures'
import { useReferralSummary } from 'hooks/api/useReferrals'
import { useCanAccess } from 'hooks/useCanAccess'
import { openReferABrandModal } from 'components/feedback/modals/ReferABrandModal/openReferABrandModal'

import {
  useSetupCompletionRewardStatus,
  type SetupBlockId,
  type SetupChecklistStatus,
} from './api/useSetupCompletionReward'

export type ChecklistItem = {
  id: string
  label: string
  /** Where to go to complete it. */
  route: string
  /** The permission that actually ENFORCES the action — see the note below. */
  permission: Permissions
  done: boolean
  /**
   * The tour that teaches this step, if one exists.
   *
   * The row's job is "go do it"; the tour's job is "here is how". Splitting those across two
   * cards made a first-run user read the checklist and Getting Started as two versions of the
   * same list. So the tour gets a second entry point ON the row, at the moment of need.
   *
   * It is an ADDITIONAL entry point, never a move. Getting Started stays, because the two
   * blocks have different scopes: this checklist is company-scoped and gated on
   * `manage_company`, tours are user-scoped and shown to everyone. Relocating tours into these
   * rows would leave a Design hire — nothing to set up, everything to learn — with no blocks
   * at all, which is the exact failure the two-block split exists to prevent.
   *
   * Optional because the catalog is one tour deep today (`TOURS = [certificatesBasics]`);
   * authoring the rest is VRG-228's deferred long pole. A row with no tour simply omits the
   * affordance rather than offering a dead control.
   */
  tourId?: string
  /**
   * VRG-655 — an ACTION row: clicking it does this instead of navigating to `route`.
   * "Refer & earn" opens its modal in place; `route` stays as where the work is reviewed.
   */
  onSelect?: () => void
}

/**
 * A block of five steps that pays out one free activation when all five complete.
 *
 * THE STAGING IS NOT COSMETIC — it resolves a circularity the single-reward version had.
 * The reward IS a free certification activation, and "activate your first certificate" is
 * itself a step, so a brand had to pay for an activation to earn the free one. Ordering the
 * blocks so that block 1's credit lands BEFORE block 2's activation step turns the reward
 * into the thing that gets them through the gate rather than a prize behind a paywall.
 *
 * It also changes what a long list costs. One reward at the end of fifteen steps — three of
 * which depend on a customer showing up — is a reward that may never arrive. Three rewards
 * at five, ten and fifteen means value lands on day one, within the week, and then whenever
 * the market does.
 *
 * ⚠️ CONSEQUENCE: item order is now load-bearing, where before it was cosmetic. Which block
 * an item sits in decides whether that block's reward is reachable this week or this quarter.
 * The three market-dependent steps (scan, lead, transfer) are CONCENTRATED in block 3 on
 * purpose — scattered across all three, every block would have a blocker.
 */
export type ChecklistBlock = {
  /**
   * `SetupBlockId`, not `string`. It always WAS one of the three at runtime (it comes
   * straight off the server's block list), and widening it to `string` meant the card could
   * not hand it back to the grant mutation without a cast — which is exactly the argument
   * that made the claim blanket rather than per-block for a while.
   */
  id: SetupBlockId
  title: string
  items: ChecklistItem[]
  completedCount: number
  total: number
  isComplete: boolean
  /** Free certification activations granted when every item in the block completes. */
  reward: number
  /** The reward is on the account — a ledger fact, not merely "all four ticked". */
  earned: boolean
  /**
   * Whether the user can finish this block by working, or has to wait for a customer.
   *
   * Block 3's steps are things that HAPPEN to a brand, not things it does — a lead needs a
   * visitor to submit an email, a transfer needs a real recipient. The copy has to be honest
   * about that rather than nagging someone to "complete" a step they cannot perform.
   */
  isEarnedOverTime?: boolean
}

export type HomeChecklist = {
  /** The three staged blocks. */
  blocks: ChecklistBlock[]
  /** Ungated extras — rendered as links, never counted, never rewarded. */
  moreItems: ChecklistItem[]
  /** Every item, flattened — the overall pill and progress bar read this. */
  items: ChecklistItem[]
  completedCount: number
  total: number
  isComplete: boolean
  isLoading: boolean
  /**
   * A single-seat company (Starter, Individual — `seats.limit === 1`), same
   * predicate `SettingsPage` already uses for its own single-seat collapse
   * (VRG-227). The card reads this to say "your account" instead of "your
   * company" — on a one-person plan "company" is a strange word for one
   * person's own setup — and to decide which of the gated/ungated items still
   * make sense to show (see `MORE_ITEMS`' "team" entry below).
   *
   * `false` while unresolved (loading, or an internal viewer with no seats
   * row) — same fail-toward-showing-more direction `SettingsPage` takes,
   * because the wrong guess here is "showed one extra link," not "hid
   * something a solo founder needed."
   */
  isSingleSeat: boolean
}

/**
 * The setup checklist — twelve steps in three blocks of four (VRG-338).
 *
 * **Derived, never self-reported.** There is no "has onboarded" flag and there must not be
 * one — the checklist completing IS the signal, so a stored flag would be a second source of
 * truth that can disagree with the data.
 *
 * **DERIVED ON THE SERVER, in ONE request.** This used to fire six client queries; twelve was
 * not a viable extension of that, for three reasons that each bite on their own:
 *
 *  1. COST — the checklist runs from the SIDEBAR (`useChecklistVisibility`), so its queries
 *     fire on every page in the app, not just Home. Twelve app-wide requests to render a card.
 *  2. PERMISSIONS — the new predicates read installations, certificates and customers, gated
 *     on `installation_view` / `view_certificates` / `view_customers`. None is
 *     `manage_company`, which is the only permission this block is gated on. A role holding
 *     `manage_company` without them would get 403s that read as `done: false` — a checklist
 *     that can never be finished, never retires, and never pays out.
 *  3. THE REWARD — `setup-completion-eligibility.ts` re-derives every predicate anyway before
 *     granting, because a free activation is money. Two derivations of the same twelve facts
 *     is two things to keep in step, and the failure is silent: the client would retire the
 *     block while the server's grant 403s.
 *
 * So the server is the single derivation and this hook is presentation — labels, routes,
 * permissions and tours over booleans it does not compute.
 */
const BLOCK_TITLES: Record<string, string> = {
  setup: 'Set up your brand',
  first_certificate: 'Get your first certificate live',
  working: 'See it working',
}

/**
 * Label, destination, gating permission and tour for each predicate key.
 *
 * The KEYS and their block grouping are the server's (`SETUP_BLOCKS`); this is only how each
 * one reads and where it sends you. Keeping the mapping here rather than on the server is
 * deliberate — copy and routes are client concerns and change far more often than the
 * predicates do.
 *
 * **Permissions are the ENFORCED ones, not the plausible ones.** `product_create`,
 * `payment_method_create` and `studio_edit` are seeded and appear in the role editor but gate
 * nothing in the API. Keying a row on one would disable it for nobody and enable it for
 * everybody. `payment_method_saved` in particular is enforced by `billing_change_plan`.
 */
const ITEM_META: Record<
  keyof SetupChecklistStatus,
  { label: string; route: string; permission: Permissions; tourId?: string }
> = {
  product: { label: 'Add your products', route: ROUTES.PRODUCTS, permission: 'manage_product', tourId: 'products-catalog-basics' },
  // "Brand your certificate page", not "Add your logo": the row routes to Studio and the step
  // is the branding pass. The PREDICATE is still the logo, because it is the only part of
  // branding that is measurable — styles are seeded, so a theme is always "configured" and
  // comparing against the default palette is the check that broke twice (VERA-304 / VRG-221).
  branding: { label: 'Style your certificates page', route: ROUTES.STUDIO, permission: 'manage_company', tourId: 'styling-your-installation' },
  installation: { label: 'Install Vera on your site', route: ROUTES.SETTINGS_COMPANY_INSTALLATION, permission: 'manage_company', tourId: 'installing-vera' },
  /*
    FEATURES, and the route must match the TOUR'S route — that is the invariant this row got
    wrong twice. It first pointed at Installation, which has no activation control at all.
    Corrected to Billing on the strength of `ActivationTriggerSelect` living in
    BillingOverview — still wrong, because that is a SECONDARY inline surface. The Features
    page owns the decision: it holds the mutation, the confirm modal, and the
    `features-activation-triggers` anchor the `activation-triggers` tour is scoped to.

    Sending a row to page A with a tour anchored on page B is not a visible crash — the tour
    just finds no anchors and falls back to a centred card, which reads as a tour that forgot
    what it was about. Whenever a row carries a tourId, its route MUST equal that tour's own
    `route`.
  */
  activationRules: { label: 'Set your activation rules', route: ROUTES.SETTINGS_COMPANY_FEATURES, permission: 'manage_company', tourId: 'activation-triggers' },

  payment: { label: 'Add a payment method', route: ROUTES.SETTINGS_COMPANY_BILLING, permission: 'billing_change_plan', tourId: 'billing-and-activations' },
  tags: { label: 'Order your tags', route: ROUTES.TAGS_SHOP, permission: 'tags_shop_purchase' },
  certificate: { label: 'Create your first certificate', route: ROUTES.CERTIFICATES, permission: 'manage_certificate', tourId: 'certificates-basics' },
  // NO TOUR. `activation-triggers` was mapped here and is wrong twice over: it is routed to
  // Features while this row goes to Certificates, and it teaches the company-wide SETTING
  // rather than how one certificate becomes active. A tour that answers a different question
  // is worse than none.
  certificateActive: { label: 'Activate your first certificate', route: ROUTES.CERTIFICATES, permission: 'manage_certificate' },

  // Block 3 is things that HAPPEN to a brand rather than things it does — a lead needs a
  // visitor to submit an email, a transfer needs a real recipient. The routes point at where
  // you would SEE it happen, not at a control that completes it.
  firstScan: { label: 'Your first scan', route: ROUTES.CERTIFICATES, permission: 'manage_certificate' },
  firstLead: { label: 'Capture a lead', route: ROUTES.OWNERS, permission: 'manage_certificate' },
  firstTransfer: { label: 'Your first transfer', route: ROUTES.CERTIFICATES, permission: 'manage_certificate' },
  // TODO(VRG-439): the server returns false until Ask records a first-ask timestamp.
  // Conversations live in localStorage (per-device, and unreadable by the server), which
  // cannot back a reward. See VRG-439-Platform.AskVera.Record.First.Ask.
  //
  // route: HOME, not SUPPORT — this row is no longer inert (VRG-227). `ask-vera-basics`
  // has no fixed `route` (its anchor is the Header launcher, present on every non-nested
  // page), so the invariant above is satisfied trivially, but the row still needs SOME
  // route to build `to=`. Home is where the row itself lives, so clicking it starts the
  // tour in place rather than navigating away first.
  askVera: { label: 'Try Ask Vera', route: ROUTES.HOME, permission: 'manage_company', tourId: 'ask-vera-basics' },
}

/**
 * "More" — worth doing, never gated and never counted.
 *
 * Rendered as links and never counted toward a block or its reward. "Refer & earn" now CAN
 * tick (VRG-655): its backing used to be a FirstPromoter iframe with no readable count, and
 * is now the native referrals API, so the row is `done` once the company has sent its first
 * referral (Spencer's open question 1 on VRG-655: SENT, not paying — it is a setup nudge,
 * and paying can take months; rewards, which never fire on a send, are where Goodhart would
 * bite). It still never counts toward `total`, so a solo brand with nobody to refer is not
 * kept from finishing setup.
 *
 * "Invite a teammate" moved here from the gated set on purpose: a solo founder has nobody to
 * invite, and gating an activation on it makes the reward unreachable for the most common
 * shape of a new brand.
 *
 * `hideForSingleSeat`, checked (VRG-227): a single-seat plan cannot invite anyone at all — not
 * "nobody to invite yet," but "the plan enforces exactly one seat" — so the row is not merely
 * unlikely to apply, it can never be completed. `defaultTag` and `referral` stay visible: a
 * solo brand still has products needing a default tag, and "refer a brand" is about referring
 * OTHER companies, not inviting a co-worker — neither mentions the company being multi-person,
 * they just happen to live in the same ungated group.
 *
 * The referral row's permission is `referrals_send` (VRG-654), every member's by default,
 * replacing `manage_company`: anyone at the brand may refer, so gating the nudge on admin
 * rights would show it disabled to the people the programme is for.
 */
const MORE_ITEMS: (ChecklistItem & { hideForSingleSeat?: boolean })[] = [
  { id: 'team', label: 'Invite a teammate', route: ROUTES.SETTINGS_COMPANY_USERS, permission: 'users_invite', done: false, hideForSingleSeat: true },
  { id: 'defaultTag', label: 'Set a default tag', route: ROUTES.PRODUCTS, permission: 'manage_product', done: false },
  { id: 'referral', label: 'Refer & earn', route: ROUTES.REFERRALS, permission: 'referrals_send', done: false, onSelect: openReferABrandModal },
]

export const useHomeChecklist = (options?: { enabled?: boolean }): HomeChecklist => {
  const enabled = options?.enabled !== false

  const { data, isLoading } = useSetupCompletionRewardStatus({ enabled })

  /**
   * SAME predicate `SettingsPage` uses for its single-seat collapse — seat limit,
   * never plan name (plans get renamed; seats are the enforced fact). `limit === 1`
   * EXACTLY; `null` (unlimited/unsynced) and any limit > 1 fall through to the
   * normal multi-seat reading. Gated on the same `enabled` this hook already
   * takes — the caller only enables either query for a company viewer who can act
   * on setup, so there is no separate internal-user check to repeat here.
   */
  const { seats } = useCompanySeats({ enabled })
  const isSingleSeat = seats?.limit === 1

  const blocks = useMemo<ChecklistBlock[]>(() => {
    const status = data?.status
    return (data?.blocks ?? []).map((block) => {
      const items: ChecklistItem[] = block.keys.map((key) => ({
        id: key,
        ...ITEM_META[key],
        done: status?.[key] ?? false,
      }))
      return {
        id: block.id,
        title: BLOCK_TITLES[block.id] ?? block.id,
        items,
        // From the LEDGER, not from the items. Complete and credited are different facts:
        // the grant is a separate write, and it can lag or fail. The card says "granted"
        // only when the activation is actually on the account.
        earned: block.earned,
        completedCount: items.filter((i) => i.done).length,
        total: items.length,
        isComplete: items.every((i) => i.done),
        reward: 1,
        // Block 3 cannot be finished by working — say so rather than nagging.
        isEarnedOverTime: block.id === 'working',
      }
    })
  }, [data])

  const items = useMemo(() => blocks.flatMap((b) => b.items), [blocks])
  const completedCount = items.filter((i) => i.done).length

  /*
    `hideForSingleSeat` items are excluded here, never left in and merely disabled — these are
    NEVER counted toward `total` in the first place (that is what makes them "more"), so hiding
    one changes nothing about the denominator the pill/progress-bar show. If a future "more"
    item DOES need to affect the count, it cannot use this flag as-is; see the block predicates
    above for how a counted item is gated instead.
  */
  // VRG-655 — the referral row ticks on the company's FIRST referral sent. Read only for a
  // member who may send (the row is disabled for anyone else, so its tick is moot).
  //
  // VRG-680 — the row exists only while the Refer & earn launch switch is on (`GET /features`,
  // off by default and off until it answers), and its summary is not fetched otherwise.
  const { enabled: referralsLive } = useReferralsEnabled()
  const { isHasAccess: canRefer } = useCanAccess({ action: 'referrals_send' })
  const { data: referralSummary } = useReferralSummary({ enabled: enabled && canRefer && referralsLive })
  const hasReferred = (referralSummary?.company.referrals ?? 0) > 0

  const moreItems = useMemo(
    () =>
      MORE_ITEMS.filter((item) => !(item.hideForSingleSeat && isSingleSeat))
        .filter((item) => referralsLive || item.id !== 'referral')
        .map((item) => (item.id === 'referral' ? { ...item, done: hasReferred } : item)),
    [isSingleSeat, hasReferred, referralsLive],
  )

  return {
    blocks,
    items,
    moreItems,
    completedCount,
    total: items.length,
    // Never "complete" while still loading, or while the server has sent no blocks at all —
    // otherwise an empty response would read as a finished checklist and retire the block.
    isComplete: !isLoading && items.length > 0 && completedCount === items.length,
    isLoading,
    isSingleSeat,
  }
}
